// AutomaticConnectionCheck.jsx
import { useEffect, useRef, useState } from "react";
import { Button, Field, HStack, Input, Popover, Portal, Separator, Text } from "@chakra-ui/react";
import { LuMapPinCheck } from "react-icons/lu";
import { Tooltip } from "../../ui/tooltip";
import { useAppStore } from "../../../context/store";
import Type from "../../../utilies/Types";
import { showToast } from "../../../utilies/Utility";
import { createLaneletChainChecker } from "../../../utilies/lanelet/Checker";

function AutomaticConnectionCheck() {
  const viewerRef = useAppStore((s) => s.viewerRef);
  const editMode = useAppStore((s) => s.editMode);
  const highlightToolsRef = useAppStore((s) => s.highlightToolsRef);
  const isActiveChecker = useAppStore((s) => s.isActiveChecker);

  const setCanOpenInfobox = useAppStore.getState().setCanOpenInfobox;
  const setCanHightlightEntity = useAppStore.getState().setCanHightlightEntity;
  const setIsActiveChecker = useAppStore.getState().setIsActiveChecker;

  const checkerRef = useRef(null);

  const [checkerDelay, setCheckerDelay] = useState(1000);
  const delayRef = useRef(100);

  const [hasStartedChecker, setHasStartedChecker] = useState(false);
  const [isRunningChecker, setIsRunningChecker] = useState(false);

  useEffect(() => {
    return () => {
      if (checkerRef.current && typeof checkerRef.current.destroy === "function") {
        checkerRef.current.destroy();
        checkerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    delayRef.current = checkerDelay;
  }, [checkerDelay]);

  return (
    <Popover.Root positioning={{ placement: "left-start" }}>
      <Popover.Trigger asChild>
        <Button
          disabled={editMode}
          userSelect={"none"}
          p={0}
          opacity={1}
          bg={isActiveChecker ? "green" : "white"}
          color={isActiveChecker ? "white" : "MenuText"}
          colorPalette={isActiveChecker ? "green" : "white"}
        >
          <Tooltip content={"Automatic Connection Check"}>
            <LuMapPinCheck size={20} />
          </Tooltip>
        </Button>
      </Popover.Trigger>

      <Portal>
        <Popover.Positioner>
          <Popover.Content>
            <Popover.Arrow />
            <Popover.Body p={2}>
              <Text fontWeight={"medium"}>Automatic Connection Check</Text>
              <Separator my={1} />

              <Field.Root mb={3} mt={3}>
                <Field.Label>Timeout (ms)</Field.Label>
                <Input
                  type="number"
                  value={checkerDelay}
                  onChange={(e) => {
                    const v = Number(e.target.value) || 0;
                    setCheckerDelay(v);
                  }}
                />
              </Field.Root>

              <HStack gap={3}>
                <Button
                  disabled={editMode}
                  variant={"solid"}
                  px={3}
                  opacity={1}
                  onClick={() => {
                    if (isActiveChecker) {
                      if (checkerRef.current && typeof checkerRef.current.destroy === "function") {
                        checkerRef.current.destroy();
                        checkerRef.current = null;
                      }

                      setIsActiveChecker(false);
                      setHasStartedChecker(false);
                      setIsRunningChecker(false);

                      setCanOpenInfobox(true);
                      setCanHightlightEntity(true);
                      return;
                    }

                    showToast("Select lanelet", Type.NOTIFICATION.INFO);

                    setCanOpenInfobox(false);
                    setCanHightlightEntity(false);
                    setIsActiveChecker(true);

                    // reset highlight tools
                    highlightToolsRef.current?.();

                    const controller = createLaneletChainChecker(viewerRef.current, {
                      getInterval: () => {
                        const v = Number(delayRef.current);
                        if (!Number.isFinite(v) || v < 0) return 100;
                        return v;
                      },
                      onStart: () => {
                        setIsActiveChecker(true);
                        setCanOpenInfobox(false);
                        setCanHightlightEntity(false);
                      },
                      onComplete: () => {
                        setIsActiveChecker(false);
                        setCanOpenInfobox(true);
                        setCanHightlightEntity(true);

                        showToast("No next lanelet was found", Type.NOTIFICATION.WARNING);

                        checkerRef.current = null;
                        setHasStartedChecker(false);
                        setIsRunningChecker(false);
                      },
                      onStateChange: ({ isRunning, started }) => {
                        setHasStartedChecker(!!started);
                        setIsRunningChecker(!!isRunning);
                      },
                    });

                    checkerRef.current = controller;
                  }}
                >
                  {isActiveChecker ? "Stop & End" : "Start"}
                </Button>

                <Button
                  variant="solid"
                  px={3}
                  opacity={1}
                  disabled={!hasStartedChecker || editMode}
                  onClick={() => {
                    const c = checkerRef.current;
                    if (!c) return;

                    if (c.isRunning()) {
                      c.pause();
                    } else {
                      c.resume();
                    }
                  }}
                >
                  {isRunningChecker ? "Pause" : "Resume"}
                </Button>
              </HStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

export default AutomaticConnectionCheck;
